import React, { useState, useEffect } from 'react'
import {
  Upload,
  Download,
  Trash2,
  Send,
  CheckCircle,
  XCircle,
  Clock,
  RefreshCw,
  Star,
  Package,
  Cpu,
  AlertCircle
} from 'lucide-react'
import { otaApi, devicesApi } from '../services/api'

interface FirmwareVersion {
  id: number
  version: string
  filename: string
  file_size: number
  checksum: string
  release_notes: string | null
  is_stable: boolean
  is_active: boolean
  download_count: number
  created_at: string
}

interface UpdateLog {
  id: number
  device_id: string | null
  device_name: string | null
  previous_version: string | null
  new_version: string
  status: string
  error_message: string | null
  started_at: string | null
  completed_at: string | null
}

interface Device {
  id: number
  device_id: string
  name: string
  status: string
}

export default function OTAPage() {
  const [versions, setVersions] = useState<FirmwareVersion[]>([])
  const [updateLogs, setUpdateLogs] = useState<UpdateLog[]>([])
  const [devices, setDevices] = useState<Device[]>([])
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [selectedDevices, setSelectedDevices] = useState<string[]>([])
  const [selectedVersion, setSelectedVersion] = useState<string>('')

  // Form upload
  const [uploadForm, setUploadForm] = useState({
    version: '',
    release_notes: '',
    is_stable: false,
    file: null as File | null
  })

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    setLoading(true)
    try {
      const [versionsRes, logsRes, devicesRes] = await Promise.all([
        otaApi.getVersions(),
        otaApi.getUpdateStatus(),
        devicesApi.list()
      ])
      setVersions(versionsRes.versions || [])
      setUpdateLogs(logsRes.updates || [])
      setDevices(devicesRes || [])
    } catch (error) {
      console.error('Error loading OTA data:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!uploadForm.file || !uploadForm.version) {
      alert('Seleziona un file e specifica la versione')
      return
    }

    setUploading(true)
    try {
      const formData = new FormData()
      formData.append('file', uploadForm.file)
      formData.append('version', uploadForm.version)
      formData.append('release_notes', uploadForm.release_notes)
      formData.append('is_stable', String(uploadForm.is_stable))

      await otaApi.uploadVersion(formData)
      alert('Versione caricata con successo!')
      setUploadForm({ version: '', release_notes: '', is_stable: false, file: null })
      loadData()
    } catch (error: any) {
      alert(`Errore upload: ${error.response?.data?.detail || error.message}`)
    } finally {
      setUploading(false)
    }
  }

  const handleSetStable = async (versionId: number, isStable: boolean) => {
    try {
      await otaApi.updateVersion(versionId, { is_stable: isStable })
      loadData()
    } catch (error) {
      console.error('Error updating version:', error)
    }
  }

  const handleDelete = async (versionId: number, versionName: string) => {
    if (!confirm(`Eliminare versione ${versionName}?`)) return

    try {
      await otaApi.deleteVersion(versionId)
      loadData()
    } catch (error) {
      console.error('Error deleting version:', error)
    }
  }

  const handleTriggerUpdate = async () => {
    if (selectedDevices.length === 0) {
      alert('Seleziona almeno un dispositivo')
      return
    }

    try {
      const result = await otaApi.triggerUpdate(selectedDevices, selectedVersion || undefined)
      alert(`Aggiornamento avviato: ${result.message}`)
      setSelectedDevices([])
      loadData()
    } catch (error: any) {
      alert(`Errore: ${error.response?.data?.detail || error.message}`)
    }
  }

  const handleTriggerUpdateAll = async () => {
    if (!confirm('Aggiornare TUTTI i dispositivi connessi?')) return

    try {
      const result = await otaApi.triggerUpdateAll(selectedVersion || undefined)
      alert(`Aggiornamento avviato su ${result.results?.length || 0} dispositivi`)
      loadData()
    } catch (error: any) {
      alert(`Errore: ${error.response?.data?.detail || error.message}`)
    }
  }

  const formatBytes = (bytes: number) => {
    if (!bytes) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('it-IT')
  }

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      pending: 'bg-yellow-100 text-yellow-800',
      downloading: 'bg-blue-100 text-blue-800',
      installing: 'bg-purple-100 text-purple-800',
      completed: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800'
    }
    const icons: Record<string, React.ReactNode> = {
      pending: <Clock className="w-3 h-3" />,
      downloading: <Download className="w-3 h-3" />,
      installing: <Package className="w-3 h-3" />,
      completed: <CheckCircle className="w-3 h-3" />,
      failed: <XCircle className="w-3 h-3" />
    }
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100'}`}>
        {icons[status]}
        {status}
      </span>
    )
  }

  const onlineDevices = devices.filter(d => d.status === 'online')

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Package className="w-7 h-7" />
          Aggiornamenti OTA
        </h1>
        <button
          onClick={loadData}
          className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Aggiorna
        </button>
      </div>

      {/* Upload nuova versione */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Upload className="w-5 h-5" />
          Carica Nuova Versione
        </h2>
        <form onSubmit={handleUpload} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Versione *
              </label>
              <input
                type="text"
                value={uploadForm.version}
                onChange={(e) => setUploadForm({ ...uploadForm, version: e.target.value })}
                placeholder="es: 1.2.3"
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                File (.tar.gz o .zip) *
              </label>
              <input
                type="file"
                accept=".gz,.tgz,.zip,application/gzip,application/x-gzip,application/zip,application/x-tar,application/x-compressed-tar"
                onChange={(e) => setUploadForm({ ...uploadForm, file: e.target.files?.[0] || null })}
                className="w-full px-3 py-2 border rounded-lg"
                required
              />
            </div>
            <div className="flex items-end gap-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={uploadForm.is_stable}
                  onChange={(e) => setUploadForm({ ...uploadForm, is_stable: e.target.checked })}
                  className="w-4 h-4 text-blue-600 rounded"
                />
                <span className="text-sm">Versione Stabile</span>
              </label>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Note di rilascio
            </label>
            <textarea
              value={uploadForm.release_notes}
              onChange={(e) => setUploadForm({ ...uploadForm, release_notes: e.target.value })}
              rows={2}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Descrivi le modifiche in questa versione..."
            />
          </div>
          <button
            type="submit"
            disabled={uploading}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {uploading ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                Caricamento...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4" />
                Carica Versione
              </>
            )}
          </button>
        </form>
      </div>

      {/* Trigger aggiornamento */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Send className="w-5 h-5" />
          Invia Aggiornamento
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Seleziona Dispositivi ({onlineDevices.length} online)
            </label>
            <div className="border rounded-lg max-h-48 overflow-y-auto">
              {devices.length === 0 ? (
                <p className="p-3 text-gray-500 text-sm">Nessun dispositivo</p>
              ) : (
                devices.map((device) => (
                  <label
                    key={device.device_id}
                    className={`flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer border-b last:border-b-0 ${
                      device.status !== 'online' ? 'opacity-50' : ''
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedDevices.includes(device.device_id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedDevices([...selectedDevices, device.device_id])
                        } else {
                          setSelectedDevices(selectedDevices.filter(id => id !== device.device_id))
                        }
                      }}
                      disabled={device.status !== 'online'}
                      className="w-4 h-4 text-blue-600 rounded"
                    />
                    <Cpu className="w-4 h-4 text-gray-400" />
                    <span className="flex-1">{device.name}</span>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      device.status === 'online' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {device.status}
                    </span>
                  </label>
                ))
              )}
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Versione Target
              </label>
              <select
                value={selectedVersion}
                onChange={(e) => setSelectedVersion(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Ultima versione stabile</option>
                {versions.filter(v => v.is_active).map((v) => (
                  <option key={v.id} value={v.version}>
                    {v.version} {v.is_stable && '⭐'}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleTriggerUpdate}
                disabled={selectedDevices.length === 0}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                <Send className="w-4 h-4" />
                Aggiorna Selezionati ({selectedDevices.length})
              </button>
              <button
                onClick={handleTriggerUpdateAll}
                disabled={onlineDevices.length === 0}
                className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50"
              >
                <Send className="w-4 h-4" />
                Aggiorna Tutti
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Lista versioni */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b">
          <h2 className="text-lg font-semibold">Versioni Disponibili</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Versione</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">File</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Dimensione</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Download</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Data</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stato</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Azioni</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {versions.map((version) => (
                <tr key={version.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-medium">{version.version}</span>
                      {version.is_stable && (
                        <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{version.filename}</td>
                  <td className="px-4 py-3 text-sm">{formatBytes(version.file_size)}</td>
                  <td className="px-4 py-3 text-sm">{version.download_count}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {formatDate(version.created_at)}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      version.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {version.is_active ? 'Attiva' : 'Disattiva'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleSetStable(version.id, !version.is_stable)}
                        className={`p-1 rounded hover:bg-gray-200 ${version.is_stable ? 'text-yellow-500' : 'text-gray-400'}`}
                        title={version.is_stable ? 'Rimuovi stabile' : 'Imposta come stabile'}
                      >
                        <Star className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(version.id, version.version)}
                        className="p-1 rounded hover:bg-gray-200 text-red-500"
                        title="Elimina"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {versions.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                    Nessuna versione disponibile
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Log aggiornamenti */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b">
          <h2 className="text-lg font-semibold">Cronologia Aggiornamenti</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Dispositivo</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Versione</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stato</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Avviato</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Completato</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Errore</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {updateLogs.map((log) => (
                <tr key={log.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div>
                      <div className="font-medium">{log.device_name || log.device_id || 'N/A'}</div>
                      <div className="text-xs text-gray-500">{log.device_id || '-'}</div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-sm">
                      {log.previous_version && (
                        <span className="text-gray-500">{log.previous_version} → </span>
                      )}
                      <span className="font-medium">{log.new_version}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">{getStatusBadge(log.status)}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {log.started_at ? formatDate(log.started_at) : '-'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {log.completed_at ? formatDate(log.completed_at) : '-'}
                  </td>
                  <td className="px-4 py-3">
                    {log.error_message && (
                      <span className="text-red-600 text-sm flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        {log.error_message}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
              {updateLogs.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                    Nessun aggiornamento registrato
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
